import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Drawer,
  Toolbar,
  IconButton,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Description as ReportsIcon,
  AutoAwesome as RecommendationsIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';

const DRAWER_WIDTH = 280;

interface SavedRecItem {
  reportId: string;
  reportType: string;
  createdAt?: string;
}

interface PlayerRecommendationsPageProps {
  portalMode?: PortalMode;
}

const PlayerRecommendationsPage: React.FC<PlayerRecommendationsPageProps> = ({ portalMode = 'player' }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';
  const [searchParams, setSearchParams] = useSearchParams();
  const reportIdFromUrl = searchParams.get('reportId');
  const reportTypeParam = searchParams.get('reportType') || 'ranking';

  const [player, setPlayer] = useState<any>(null);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [savedList, setSavedList] = useState<SavedRecItem[]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Dialog: open on same page when clicking a saved report (or when URL has reportId)
  const [dialogReport, setDialogReport] = useState<{ reportId: string; reportType: string } | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogResult, setDialogResult] = useState<{ combinedContent: string; perItem: Array<{ item: string; recommendation: string }> } | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    fetch(`/api/players/me?userId=${user.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data?.id) setPlayer(data.data);
        setPlayerLoaded(true);
      })
      .catch(() => setPlayerLoaded(true));
  }, [user?.id]);

  useEffect(() => {
    if (!player?.id) return;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    fetch(`/api/ai/recommendations/list?playerId=${player.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setSavedList(data.data);
        }
        setListLoaded(true);
      })
      .catch(() => setListLoaded(true));
  }, [player?.id]);

  // Open dialog when URL has reportId (e.g. shared link)
  useEffect(() => {
    if (reportIdFromUrl && player?.id) {
      setDialogReport({ reportId: reportIdFromUrl, reportType: reportTypeParam });
    }
  }, [reportIdFromUrl, reportTypeParam, player?.id]);

  // Fetch recommendation when dialog is open
  useEffect(() => {
    if (!dialogReport || !player?.id) {
      setDialogResult(null);
      setDialogError(null);
      return;
    }
    setDialogResult(null);
    setDialogError(null);
    setDialogLoading(true);
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    fetch(`/api/ai/recommendations?playerId=${player.id}&reportId=${encodeURIComponent(dialogReport.reportId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then((data) => {
        if (data.success) {
          setDialogResult({
            combinedContent: data.combinedContent || '',
            perItem: Array.isArray(data.perItem) ? data.perItem : [],
          });
        } else {
          setDialogError(data.message || 'Failed to load');
        }
      })
      .catch(() => setDialogError('No saved AI Assistant report for this report. Go to Reports and click the AI Assistant icon on a report to generate one.'))
      .finally(() => setDialogLoading(false));
  }, [dialogReport?.reportId, dialogReport?.reportType, player?.id]);

  const drawer = <PlayerSidebar />;

  const mainContent = () => {
    if (!playerLoaded) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress />
          <Typography>Loading…</Typography>
        </Box>
      );
    }
    if (!player?.id) {
      return (
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">
            Could not load your player profile. You may need to complete registration as a player.
          </Alert>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(paths.dashboard)} sx={{ mt: 2 }}>
            Back to Dashboard
          </Button>
        </Box>
      );
    }

    if (!listLoaded) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress />
          <Typography>Loading…</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          AI Assistant
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Reports that have AI Assistant recommendations. Click one to view. Generate new ones from Reports by clicking the AI Assistant icon on a report.
        </Typography>
        {savedList.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No saved AI Assistant reports yet. Go to <strong>Reports</strong>, click the AI Assistant icon on a report row to generate one; it will be saved and appear here next time. (Reports generated before this feature was added were not stored.)
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {savedList.map((item) => (
              <Card key={item.reportId} variant="outlined">
                <CardActionArea
                  onClick={() => setDialogReport({ reportId: item.reportId, reportType: item.reportType })}
                  sx={{ p: 2 }}
                >
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <RecommendationsIcon sx={{ color: '#ff9800' }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.reportType === 'ranking' ? 'Ranking' : 'Training'} report
                      </Typography>
                      <Chip label={item.reportId} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                      {item.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
        <Button variant="outlined" startIcon={<ReportsIcon />} onClick={() => navigate(paths.reports)} sx={{ mt: 3 }}>
          Open Reports
        </Button>
      </Box>
    );
  };

  const pageBody = (
    <>
        <Container maxWidth="md" sx={{ pt: hideSidebar ? 2 : 0 }}>{mainContent()}</Container>

        <Dialog
          open={!!dialogReport}
          onClose={() => { setDialogReport(null); setSearchParams({}); }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <span>AI Assistant</span>
            <IconButton size="small" onClick={() => { setDialogReport(null); setSearchParams({}); }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {dialogReport && (
              <>
                {dialogLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography>Loading…</Typography>
                  </Box>
                )}
                {!dialogLoading && dialogError && (
                  <Box sx={{ py: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>{dialogError}</Alert>
                    <Button variant="contained" startIcon={<ReportsIcon />} onClick={() => navigate(paths.reports)}>
                      Open Reports
                    </Button>
                  </Box>
                )}
                {!dialogLoading && dialogResult && (
                  <Box sx={{ py: 1 }}>
                    {dialogResult.perItem && dialogResult.perItem.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                          By item
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {dialogResult.perItem.map((p, i) => (
                            <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                              <strong>{p.item}:</strong> {p.recommendation}
                            </Typography>
                          ))}
                        </Box>
                      </Paper>
                    )}
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Recommendations &amp; practice
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {dialogResult.combinedContent || '—'}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
    </>
  );

  if (hideSidebar) {
    return pageBody;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          display: { xs: 'block', md: 'none' },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        {drawer}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 2, pt: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar disableGutters sx={{ minHeight: 48 }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {pageBody}
      </Box>
    </Box>
  );
};

export default PlayerRecommendationsPage;
